import { memo, useCallback, useEffect, useRef } from 'react'

import { MediaPlatformEdit } from '@nativewaves/nw-cloud-sdk/media'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import { useMediaLibraryUpdateMutation } from 'hooks/mutations/publishing'
import { mediaLibraryConfigRoute } from 'pages/NWPlatform/MediaLibraries/MediaLibrary'
import { mediaLibraryQueryKeys } from 'services/queryKeys'

const JSONRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const editorInstanceRef = useRef<EditorInstance>(undefined)
  const queryClient = useQueryClient()

  const { environmentId, mediaLibraryId } = mediaLibraryConfigRoute.useParams()

  const mediaLibraryQuery = useQuery({
    ...mediaLibraryQueryKeys.detail({ libraryId: mediaLibraryId }),
    initialData: queryClient
      .getQueryData(mediaLibraryQueryKeys.list({ environmentId }).queryKey)
      ?.pages.flatMap((page) => page.items)
      .find((item) => item.id === mediaLibraryId),
  })
  const { data: mediaLibrary } = mediaLibraryQuery

  const { mutate: updateMediaLibrary } = useMediaLibraryUpdateMutation()

  const handleFormSubmit = useCallback(
    (data: MediaPlatformEdit) => {
      updateMediaLibrary(
        { libraryId: mediaLibraryId, mediaPlatformEdit: data },
        {
          onSuccess: () => {
            enqueueSnackbar(t('domain:MediaLibrary.mediaLibrarySaved'), {
              variant: 'success',
            })
          },
        },
      )
    },
    [enqueueSnackbar, mediaLibraryId, t, updateMediaLibrary],
  )

  useEffect(() => {
    if (editorInstanceRef.current && mediaLibraryQuery.isSuccess) {
      editorInstanceRef.current?.getModel()?.setValue(window.JSON.stringify(mediaLibrary, null, 4))
    }
  }, [mediaLibraryQuery.isSuccess, mediaLibrary])

  if (mediaLibraryQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!mediaLibrary) {
    return <NotFound />
  }

  return (
    <Editor
      value={mediaLibrary}
      onSubmit={handleFormSubmit}
      getEditorInstance={(editorInstance: EditorInstance) => (editorInstanceRef.current = editorInstance)}
    />
  )
}

export const JSON = memo(JSONRaw)
